import React, { memo, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import {
  Paper,
  Stack,
  Button,
  Tooltip,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { motion } from "framer-motion";
import configServ from "../../../services/config";
import { useEffect } from "react";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import AlertDialog from "../../AlertDialog/AlertDialog";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import RequiredLabel from "../../labels/RequiredLabel";
import Swal from "sweetalert2";

//FUNCTION
const GroupCard = ({ isChanged, setIsChanged, editData }) => {
  //#region code
  const dispatch = useDispatch();
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const [formData, setFormData] = useState({
    status: true,
    isPrimary: false,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [category, setCategory] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchCategory();
  }, []);

  const fetchCategory = async () => {
    try {
      const dataToSend = {
        company_id: company_id,
        search_by: null,
        search: null,
        per_page: null,
        page: null,
      };
      const res = await configServ.getcategory(dataToSend);
      if (res.status === 200) {
        setCategory(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    //Update form data
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    // Reset the validation error when the user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prevValidationError) => ({
        ...prevValidationError,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      dispatch(triggerLoader());

      const error = handleFormValidation();
      if (error === false) {
        return false;
      }
      setValidationErrors({});

      const dataToSend = {
        ...formData,
        company_id: company_id,
      };

      if (!isEdit) {
        const res = await configServ.addGroup(dataToSend);
        if (res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.message,
          });
        } else if (res.status === 400) {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text: res.message || "Failed to create sub-category.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.message || "Failed to create sub-category.",
          });
        }
      } else {
        const res = await configServ.editGroup(dataToSend);
        if (res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.message,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.message || "Failed to update sub-category.",
          });
        }
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error has occurred.",
      });
    } finally {
      dispatch(triggerLoader());
    }
  };

  const handleFormValidation = () => {
    const errors = {};
    if (!formData.group_name) {
      errors.group_name = "required";
    }
    if (!formData.category_id) {
      errors.category_id = "required";
    }
    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  useEffect(() => {
    if (Object.keys(editData).length !== 0) {
      setFormData(editData);
      setIsEdit(true);
      setFormData((prev) => ({
        ...prev,
        status: true,
      }));
    }
  }, [editData]);

  const clear = () => {
    setFormData({
      status: true,
      isPrimary: false,
    });
    setIsEdit(false);
  };
  //#endregion

  return (
    <>
      <motion.div
        style={{
          width: "100%",
          backgroundColor: "#e3f3fc",
          padding: 10,
          borderRadius: "10px",
          border: "none",
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1, delay: 0.1 }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="flex-start"
          spacing={1}
        >
          <Grid item xs={12} md={12} justifyContent={"flex-start"}>
            <Typography sx={{ fontSize: "16px", fontWeight: "bolder" }}>
              Sub-Category Detail
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="group_name"
              name="group_name"
              label={<RequiredLabel label={"Sub-Category"} />}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ backgroundColor: "white" }}
              onChange={handleChange}
              error={validationErrors.group_name}
              value={formData.group_name || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              sx={{ backgroundColor: "white" }}
              size="small"
              label="Description"
              name="description"
              multiline
              rows={2}
              onChange={handleChange}
              value={formData.description || ""}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              fullWidth
              size="small"
              sx={{ backgroundColor: "white" }}
            >
              <InputLabel error={!!validationErrors.category_id}>
                Category
              </InputLabel>
              <Select
                fullWidth
                size="small"
                value={formData.category_id || ""}
                onChange={(event) =>
                  handleChange({
                    target: { name: "category_id", value: event.target.value },
                  })
                }
                label="Category"
                error={!!validationErrors.category_id}
              >
                {category.map((item) => (
                  <MenuItem key={item.categoryid} value={item.categoryid}>
                    {item.categoryname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="space-between">
          <Grid
            item
            xs={12}
            md={6}
            justifyContent="flex-end"
            alignItems="center"
          >
            <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                name="status"
                control={
                  <Checkbox checked={Boolean(formData.status) || false} />
                }
                onChange={handleChangeCheckbox}
                label="Hide/Unhide"
                //style={{ marginRight: '20px' }}
              />
              <FormControlLabel
                name="isPrimary"
                control={
                  <Checkbox checked={Boolean(formData.isPrimary) || false} />
                }
                onChange={handleChangeCheckbox}
                label="Default"
              />
            </FormGroup>
          </Grid>
          <Grid
            container
            item
            xs={12}
            md={6}
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <Button
                onClick={clear}
                variant="contained"
                color="secondary"
                startIcon={<RotateLeftIcon />}
              >
                Reset
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleSubmit}
                autoFocus
                variant="contained"
                color={isEdit ? "success" : "primary"}
                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </motion.div>
    </>
  );
};

export default memo(GroupCard);
